import { z } from "zod";
import { pagedResponse, paginationQuerySchema } from "../common/pagination.js";
const TASK_STATUS = z.enum([
    "created",
    "queued",
    "assigned",
    "in_progress",
    "completed",
    "failed",
    "cancelled",
    "skipped",
]);
const TASK_TYPE = z.enum([
    "scan_zone",
    "scan_fixture",
    "scan_shelf",
    "scan_category",
    "recapture",
    "return_to_base",
    "manual_capture",
    "other",
]);
const NAVIGATION_TYPE = z.enum(["none", "manual", "waypoint", "zone", "fixture", "shelf"]);
// One fixture/zone/store target of a capture task (a task may have 0..N).
export const captureTaskTarget = z.object({
    fixtureId: z.string().uuid().nullable(),
    fixtureCode: z.string().nullable(),
    fixtureName: z.string().nullable(),
    storeId: z.string().uuid().nullable(),
    zoneId: z.string().uuid().nullable(),
    orderIndex: z.number().int().nullable(),
});
export const captureTaskInfo = z.object({
    id: z.string().uuid(),
    sessionId: z.string().uuid(),
    // Nullable: ad-hoc tasks (created directly on a session without a fixture
    // target) legitimately have no associated store. Auto-generated tasks for
    // mobile sessions still populate this via capture_task_targets.
    storeId: z.string().uuid().nullable(),
    fixtureId: z.string().uuid().nullable(),
    fixtureCode: z.string().nullable(),
    fixtureName: z.string().nullable(),
    categoryId: z.string().uuid().nullable(),
    categoryName: z.string().nullable(),
    assignedToUserId: z.string().uuid().nullable(),
    taskType: TASK_TYPE,
    status: TASK_STATUS,
    navigationType: NAVIGATION_TYPE,
    goalPoints: z.array(z.record(z.unknown())),
    // Server-tracked retry attempt depth (parent.retry_count + 1 on each retry).
    // TMS reads this to enforce its retry cap.
    retryCount: z.number().int(),
    // The failed/cancelled task this one was spawned from via the retry route,
    // or null for an original task. Drives the retry-lineage view in the UI.
    parentTaskId: z.string().uuid().nullable(),
    // All targets of the task (the flattened fixtureId/* above is the first one).
    targets: z.array(captureTaskTarget),
    sortOrder: z.number().int().nullable(),
    startedAt: z.string().datetime().nullable(),
    finishedAt: z.string().datetime().nullable(),
    metadata: z.record(z.unknown()),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const createTaskRequest = z.object({
    sessionId: z.string().uuid(),
    taskType: TASK_TYPE.default("scan_fixture"),
    categoryId: z.string().uuid().nullish(),
    fixtureIds: z.array(z.string().uuid()).default([]),
    navigationType: NAVIGATION_TYPE.default("none"),
    goalPoints: z.array(z.record(z.unknown())).default([]),
    instructions: z.string().max(2000).nullish(),
    sortOrder: z.number().int().min(0).default(0),
    metadata: z.record(z.unknown()).default({}),
});
export const updateTaskRequest = z
    .object({
    status: TASK_STATUS.optional(),
    assignedToUserId: z.string().uuid().nullish(),
    startedAt: z.string().datetime().nullish(),
    finishedAt: z.string().datetime().nullish(),
    navigationType: NAVIGATION_TYPE.optional(),
    goalPoints: z.array(z.record(z.unknown())).optional(),
    metadata: z.record(z.unknown()).optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: "At least one field required" });
/**
 * Body for ad-hoc task creation on a session (used by the "upload to scan"
 * flow at `POST /api/v2/capture-sessions/:sessionId/tasks`). All fields
 * optional — default task_type is "manual_capture", no fixture/category
 * linkage. Distinct from {@link createTaskRequest} which is the rich
 * top-level POST shape.
 */
export const createAdHocTaskRequest = z
    .object({
    taskType: TASK_TYPE.optional(),
    instructions: z.string().max(2000).optional(),
    metadata: z.record(z.unknown()).optional(),
})
    .default({});
export const listTasksQuery = paginationQuerySchema.extend({
    sessionId: z.string().uuid().optional(),
    status: TASK_STATUS.optional(),
    fixtureId: z.string().uuid().optional(),
    assignedToUserId: z.string().uuid().optional(),
});
export const captureTasksListResponse = pagedResponse(captureTaskInfo);
//# sourceMappingURL=capture-tasks.schemas.js.map